import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Boxes, Plus, SquarePen } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { useCurrentUser } from '@/lib/auth-context';

import { WorkPackageCreateDialog } from './WorkPackageCreateDialog';
import { AFFECTS_FLAGS, affectsFor, canAuthorWorkPackages, resolveI18nText } from './helpers';
import { useWorkPackagesList } from './useWorkPackages';
import type { WorkPackageDto } from './types';

const NS = 'settings';

export const WorkPackagesPage = () => {
  const { t, i18n } = useTranslation(NS);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canAuthor = canAuthorWorkPackages(user);
  const query = useWorkPackagesList();
  const [createOpen, setCreateOpen] = useState(false);

  // Platform authoring lists both active and inactive packages.
  const rows = query.data ?? [];

  const columns = useMemo<MasterTableColumn<WorkPackageDto>[]>(
    () => [
      {
        key: 'name',
        header: t('efs.settings.work_packages.fields.name'),
        cell: (pkg) => (
          <div>
            <div className="font-medium text-efs-text-primary">
              {resolveI18nText(pkg.name_i18n, i18n.language) || pkg.package_code}
            </div>
            <div className="mt-1 break-all text-xs text-efs-text-muted">{pkg.package_code}</div>
          </div>
        ),
      },
      {
        key: 'party_type',
        header: t('efs.settings.work_packages.fields.party_type'),
        cell: (pkg) =>
          pkg.party_type
            ? t(`efs.settings.companies.types.${pkg.party_type}`, {
                defaultValue: pkg.party_type,
              })
            : '—',
      },
      {
        key: 'affects',
        header: t('efs.settings.work_packages.fields.affects'),
        cell: (pkg) => {
          const active = AFFECTS_FLAGS.filter((flag) => affectsFor(pkg, flag));
          if (active.length === 0) return <span className="text-xs text-efs-text-muted">—</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {active.map((flag) => (
                <Badge key={flag} variant="secondary">
                  {t(`efs.settings.work_packages.affects.${flag}`)}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        key: 'permissions',
        header: t('efs.settings.work_packages.fields.permission_count'),
        align: 'right',
        cell: (pkg) =>
          t('efs.settings.work_packages.summary.permissions', {
            count: pkg.permission_codes.length,
          }),
      },
      {
        key: 'status',
        header: t('efs.settings.work_packages.fields.status'),
        cell: (pkg) =>
          pkg.active_flag ? (
            <Badge variant="default">{t('efs.settings.work_packages.active')}</Badge>
          ) : (
            <Badge variant="outline">{t('efs.settings.work_packages.inactive')}</Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (pkg) =>
          canAuthor ? (
            <Button asChild variant="outline" size="sm">
              <Link to={`/settings/work-packages/${encodeURIComponent(pkg.work_package_id)}/edit`}>
                <SquarePen className="h-4 w-4" />
                {t('efs.settings.work_packages.actions.edit')}
              </Link>
            </Button>
          ) : null,
      },
    ],
    [canAuthor, i18n.language, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.work_packages.title')}
        subtitle={t('efs.settings.work_packages.subtitle')}
        count={rows.length}
        actions={
          canAuthor ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.settings.work_packages.actions.create')}
            </Button>
          ) : null
        }
      />

      <section className="rounded-md border border-efs-border bg-efs-bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
              <Boxes className="h-4 w-4" />
              {t('efs.settings.work_packages.package_list')}
            </div>
            <p className="mt-1 text-xs text-efs-text-muted">
              {t('efs.settings.work_packages.list_hint')}
            </p>
          </div>
        </div>
        <TableToolbar
          count={t('efs.common.messages.count', {
            ns: 'common',
            defaultValue: '{{count}} 件',
            count: rows.length,
          })}
        />
        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(pkg) => pkg.work_package_id}
          isLoading={query.isLoading}
          emptyMessage={t('efs.settings.work_packages.empty')}
        />
      </section>

      <WorkPackageCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(pkg) =>
          navigate(`/settings/work-packages/${encodeURIComponent(pkg.work_package_id)}/edit`)
        }
      />
    </div>
  );
};

export default WorkPackagesPage;
